import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppHeaderComponent } from './shared/components/app-header/app-header.component';
import { RouteObserver } from './shared/services/route-observer/route-observer.service';
import { InsightsService } from './shared/services/insights/insights.service';
import { FeatureFlagsService } from 'src/app/shared/services/feature-flags/feature-flags.service';
import { NetworkAvailabilityDialogComponent } from './shared/features/network-availability-dialog/dialog/network-availability-dialog.component';
import { NetworkAvailabilityHandlerService } from './shared/features/network-availability-dialog/services/network-availability-handler.service';
import { ApiNames } from './shared/features/network-availability-dialog/settings/settings';
import { AuthService, User } from '@auth0/auth0-angular';
import { UrlParamsEffect } from './routes/routes-with-sidebar/shared/state/url-params/url-params.effect';
import { PermissionsService } from '@r1-development/rps-shared-ui';
import { PendoService } from './shared/services/pendo/pendo.service';
import { filter, switchMap, take, tap } from 'rxjs';
import { UserIdleService } from './shared/services/user-idle/user-idle.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConfigService } from './shared/app-config/app-config.service';
import { LAST_LOGGED_IN_AS } from './shared/models';
import { MetricsService } from './shared/services/metrics/metrics.service';
import { UserService } from './routes/toolbox/services/user/user.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  imports: [RouterOutlet, AppHeaderComponent],
  selector: 'ri-resolution-ui-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly appConfigService = inject(AppConfigService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly insightsService = inject(InsightsService);
  private readonly networkAvailabilityHandlerService = inject(
    NetworkAvailabilityHandlerService
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly metricsService = inject(MetricsService);
  private readonly pendoService = inject(PendoService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly routeObserver = inject(RouteObserver);
  private readonly urlParamsEffect = inject(UrlParamsEffect);
  private readonly userIdleService = inject(UserIdleService);
  private readonly userService = inject(UserService);

  private readonly authTokenKeyPrefix = '@@auth0spajs@@';
  private isNetworkErrorDialogOpen = false;
  private readonly userData = signal<User | null>(null);
  private readonly _user = signal<User | undefined>(undefined);
  private readonly errors = this.networkAvailabilityHandlerService.errorSignal;

  readonly utcDate = signal<string>('');
  public readonly hasError = computed(() => {
    return this.errors().length > 0;
  });

  // Hidden on the file preview route. Seeded from the current path
  // so a freshly opened preview tab doesn't flash the header while the route's
  // guards and lazy chunk load (i.e. before NavigationEnd fires).
  private readonly _hideHeader = signal<boolean>(
    location.pathname.startsWith('/preview/')
  );
  public readonly hideHeader = this._hideHeader.asReadonly();

  constructor() {
    this.userService.subscribeToMe();
    this.subscribeToUserInitializedTheFirstTime();
    this.authService.user$.subscribe((user: User | null | undefined) => {
      this._user.set(user ?? undefined);
      if (user?.email) {
        this.insightsService.setAuthenticatedUserContext(user.email);
        this.metricsService.setUserId(user.sub ?? null);
      } else {
        this.insightsService.clearAuthenticatedUserContext();
      }
    });

    // update activeTaskGroupId state on url change.
    this.urlParamsEffect.registerUrlParamsTaskGroupIdChanges();
    this.urlParamsEffect.registerUrlTopLevelTaskGroupsChanges();
    // start listening angular router navigation
    this.routeObserver.observe();
    this.trackFilePreviewRoute();

    const idleMinutes = this.appConfigService.config.idleTimeoutMinutes || 30;
    this.userIdleService.setConfigValues({
      idle: idleMinutes * 60,
      timeout: 0,
    });
    this.userIdleService.startWatching();

    this.authService.error$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (err) => {
          if (
            err?.message?.toLowerCase().includes('missing refresh token') &&
            location.pathname !== '/logged-out'
          ) {
            this.removeAuthKeysFromLocalStorage();
            this.performLogout();
          }
        },
      });

    this.userIdleService
      .onTimerStart()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.userIdleService.stopWatching();
        this.removeAuthKeysFromLocalStorage();
        this.performLogout();
      });

    this.trackApiAvailabilityError();
  }

  // Hides the app header on the file preview route.
  private trackFilePreviewRoute(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this._hideHeader.set(event.urlAfterRedirects.startsWith('/preview/'));
      });
  }

  private openNetworkErrorDialog() {
    if (this.isNetworkErrorDialogOpen) {
      return;
    }

    this.isNetworkErrorDialogOpen = true;

    const dialogRef = this.dialog.open(NetworkAvailabilityDialogComponent, {
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isNetworkErrorDialogOpen = false;
      });
  }

  private subscribeToUserInitializedTheFirstTime() {
    // App component is not routable component. So it is not protected by
    // AuthGuard from Auth0 lib. It means that we shouldn't run any API
    // requests until user will be authenticated. Otherwise we will
    // initiate API requests (to fill UI states with data), but those
    // requests fail with 401 error. It is happening almost instantly,
    // just flash for a 1 second. But during this short period of time
    // there are multiple requests are already sent to API, then
    // Auth0 finds that user is not authenticated and uses "redirect_uri"
    // to navigate to another page. Even though API requests disappear
    // from Network tab of the Dev Tools, basically they are not gone
    // on the browser level if they were already sent. Besides that
    // server side may receive them.
    this.authService.user$
      .pipe(
        filter(Boolean),
        take(1),
        tap(() => {
          this.permissionsService.loadPermissions();

          this.networkAvailabilityHandlerService.checkAvailability([
            ApiNames.AccountLink,
            ApiNames.MechanicalTurk,
          ]);

          this.featureFlagsService.start();
        }),
        switchMap((user) =>
          this.pendoService.isPendoScriptLoaded.pipe(
            filter((isLoaded) => isLoaded),
            take(1),
            tap(() => this.pendoService.initialize(user))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private performLogout() {
    const email = this._user()?.email;
    if (email) {
      localStorage.setItem(LAST_LOGGED_IN_AS, email);
    } else {
      localStorage.removeItem(LAST_LOGGED_IN_AS);
    }

    this.authService.logout({
      logoutParams: {
        returnTo: location.origin + '/logged-out',
        federated: false,
      },
    });
  }

  private removeAuthKeysFromLocalStorage() {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.authTokenKeyPrefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  private trackApiAvailabilityError(): void {
    effect(() => {
      const hasError = this.hasError();

      untracked(() => {
        if (hasError && !this.isNetworkErrorDialogOpen) {
          this.openNetworkErrorDialog();
        }
      });
    });
  }
}
